import React, { useState, useEffect } from 'react';
import { useWallet } from './hooks/useWallet';
import ParticleBackground from './components/ParticleBackground';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import Home from './components/Home';
import Team from './components/Team';
import Rewards from './components/Rewards';
import Registration from './components/Registration';
import TelegramNotify from './components/TelegramNotify';
import { ACTIVITY_TYPES } from './config';
import './App.css';

const TOUR_STEPS = [
  { title: 'Welcome to AlphaMeta', description: 'Your AI-powered DeFi trading platform. Let us show you around!', gif: '/1.gif' },
  { title: 'AI Trading Bots', description: 'Each investment is managed by an AI trading bot with 0.5% - 1% daily profit and up to 300% total returns.', gif: '/2.gif' },
  { title: 'Build Your Team', description: 'Earn 5% direct referral bonus and level income across 15 levels of your network.', gif: '/4.gif' },
  { title: 'Earn Club Rewards', description: 'Achieve club levels to earn bonus rewards up to $10,000!', gif: '/5.gif' },
  { title: "You're Ready!", description: 'Register and start your trading journey with AlphaMeta.', gif: '/3.gif' }
];

function App() {
  const [activePage, setActivePage] = useState('home');
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [allActivities, setAllActivities] = useState([]);
  const [activityPage, setActivityPage] = useState(0);
  const [loadingActivities, setLoadingActivities] = useState(false);

  const wallet = useWallet();

  useEffect(() => {
    const hasSeen = localStorage.getItem('alphameta_tour_seen');
    if (!hasSeen) setTimeout(() => setShowTour(true), 1500);
    setTimeout(() => setIsReady(true), 500);

    const urlParams = new URLSearchParams(window.location.search);
    const refId = urlParams.get('ref');
    if (refId) localStorage.setItem('alphameta_referrer', refId);
  }, []);

  const addNotification = (type, message) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 4000);
  };

  const handleStartTour = () => { setTourStep(0); setShowTour(true); };
  const handleNextStep = () => tourStep < TOUR_STEPS.length - 1 ? setTourStep(tourStep + 1) : handleCloseTour();
  const handlePrevStep = () => tourStep > 0 && setTourStep(tourStep - 1);
  const handleCloseTour = () => { setShowTour(false); localStorage.setItem('alphameta_tour_seen', 'true'); };

  const handleViewAllActivities = async () => {
    setShowActivitiesModal(true);
    setActivityPage(0);
    setLoadingActivities(true);
    try {
      const { activities } = await wallet.fetchActivities(0, 20);
      setAllActivities(activities);
    } catch (error) {
      addNotification('error', 'Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const loadMoreActivities = async () => {
    const nextPage = activityPage + 1;
    setLoadingActivities(true);
    try {
      const { activities } = await wallet.fetchActivities(nextPage, 20);
      setAllActivities(prev => [...prev, ...activities]);
      setActivityPage(nextPage);
    } catch (error) {
      addNotification('error', 'Failed to load more activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const formatTime = (ts) => new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const renderPage = () => {
    const commonProps = { addNotification };
    
    switch (activePage) {
      case 'home':
        return <Home userId={wallet.userId} userData={wallet.userData} userInvestments={wallet.userInvestments} tokenBalance={wallet.tokenBalance} userActivities={wallet.userActivities} totalActivityCount={wallet.totalActivityCount} invest={wallet.invest} startTrade={wallet.startTrade} claimTradeROI={wallet.claimTradeROI} claimRewards={wallet.claimRewards} refreshData={wallet.refreshData} onViewAllActivities={handleViewAllActivities} contract={wallet.contract} {...commonProps} />;
      case 'team':
        return <Team userId={wallet.userId} uplineId={wallet.uplineId} userData={wallet.userData} contract={wallet.contract} fetchLevelUsers={wallet.fetchLevelUsers} {...commonProps} />;
      case 'rewards':
        return <Rewards userId={wallet.userId} userData={wallet.userData} userActivities={wallet.userActivities} contract={wallet.contract} claimRewards={wallet.claimRewards} refreshData={wallet.refreshData} {...commonProps} />;
      default:
        return null;
    }
  };

  const showRegistration = wallet.account && !wallet.isRegistered;
  const showMain = wallet.account && wallet.isRegistered;

  return (
    <div className={`app ${isReady ? 'ready' : ''}`}>
      <ParticleBackground />
      <div className="scanline-overlay" />
      
      <Header account={wallet.account} isConnecting={wallet.isConnecting} connectWallet={wallet.connectWallet} disconnectWallet={wallet.disconnectWallet} onTourStart={handleStartTour} onTelegramOpen={() => setShowTelegramModal(true)} showTelegram={showMain} />

      <main className="main-content">
        {!wallet.account && (
          <Registration account={wallet.account} tokenBalance={wallet.tokenBalance} registerAndInvest={wallet.registerAndInvest} addNotification={addNotification} connectWallet={wallet.connectWallet} isConnecting={wallet.isConnecting} />
        )}
        {showRegistration && (
          <Registration account={wallet.account} tokenBalance={wallet.tokenBalance} registerAndInvest={wallet.registerAndInvest} addNotification={addNotification} connectWallet={wallet.connectWallet} isConnecting={wallet.isConnecting} />
        )}
        {showMain && renderPage()}
      </main>

      {showMain && <BottomNav activePage={activePage} setActivePage={setActivePage} />}

      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(n => (
          <div key={n.id} className={`notification ${n.type}`}>
            {n.type === 'success' ? '✓' : '✕'} {n.message}
          </div>
        ))}
      </div>

      {/* Tour Modal */}
      {showTour && (
        <div className="tour-overlay">
          <div className="tour-backdrop" onClick={handleCloseTour} />
          <div className="tour-modal">
            <button className="tour-close" onClick={handleCloseTour}>✕</button>
            <div className="tour-content">
              <img src={TOUR_STEPS[tourStep].gif} alt="" className="tour-gif" />
              <h2 className="tour-title">{TOUR_STEPS[tourStep].title}</h2>
              <p className="tour-desc">{TOUR_STEPS[tourStep].description}</p>
              <div className="tour-dots">
                {TOUR_STEPS.map((_, i) => <div key={i} className={`tour-dot ${i === tourStep ? 'active' : ''}`} />)}
              </div>
              <div className="tour-actions">
                {tourStep > 0 && <button className="tour-btn secondary" onClick={handlePrevStep}>Back</button>}
                <button className="tour-btn primary" onClick={handleNextStep}>
                  {tourStep === TOUR_STEPS.length - 1 ? "Let's Go!" : 'Next'}
                </button>
              </div>
              {tourStep === 0 && <button className="tour-skip" onClick={handleCloseTour}>Skip Tour</button>}
            </div>
          </div>
        </div>
      )}

      {/* Activities Modal */}
      {showActivitiesModal && (
        <div className="modal-overlay" onClick={() => setShowActivitiesModal(false)}>
          <div className="activities-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>All Activities</h2>
              <button className="modal-close" onClick={() => setShowActivitiesModal(false)}>✕</button>
            </div>
            <div className="activities-scroll">
              {allActivities.map((act, i) => {
                const type = ACTIVITY_TYPES[act.activityType] || { label: 'Unknown', icon: '❓', color: '#888' };
                return (
                  <div key={i} className="activity-row" style={{ '--accent': type.color }}>
                    <div className="ar-icon">{type.icon}</div>
                    <div className="ar-info">
                      <span className="ar-type">{type.label}</span>
                      <span className="ar-time">{formatTime(act.timestamp)}</span>
                    </div>
                    <span className="ar-amount" style={{ color: type.color }}>+${parseFloat(act.amount).toFixed(2)}</span>
                  </div>
                );
              })}
              {loadingActivities && <div className="loading-more"><span className="spinner" /></div>}
            </div>
            {allActivities.length >= (activityPage + 1) * 20 && (
              <button className="load-more-btn" onClick={loadMoreActivities} disabled={loadingActivities}>
                Load More
              </button>
            )}
          </div>
        </div>
      )}

      {/* Telegram Modal */}
      {showTelegramModal && (
        <TelegramNotify
          userId={wallet.userId}
          userData={wallet.userData}
          userInvestments={wallet.userInvestments}
          onClose={() => setShowTelegramModal(false)}
          addNotification={addNotification}
        />
      )}

      {/* Error Toast */}
      {wallet.error && (
        <div className="error-toast animate-fade-in-up">
          ✕ {wallet.error}
        </div>
      )}
    </div>
  );
}

export default App;
